import { reportHandledWebClientError } from "@/lib/client-error-reporting";
import { sanitizeUserFacingErrorMessage } from "@shared/utils/userFacingErrors";

const CONVEX_UNCAUGHT_PREFIX = /^Uncaught\s+ConvexError:\s*/i;
const CONVEX_CLASS_PREFIX = /^ConvexError:\s*/i;
const GENERIC_ERROR_PREFIX = /^Error:\s*/i;
const CONVEX_ERROR_CODE_PREFIX = /^[A-Z_]+:\s*/;
const CONVEX_DETAILS_SUFFIX = /\s*\|\s*\{[\s\S]*\}\s*$/;

export interface NormalizedOtpError {
  message: string;
  suggestsResend: boolean;
  isExpired: boolean;
  isAlreadyUsed: boolean;
}

function extractErrorMessage(error: unknown): string | null {
  if (typeof error === "string") {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "object" && error !== null) {
    const possibleMessage = (error as { message?: unknown }).message;
    if (typeof possibleMessage === "string") {
      return possibleMessage;
    }

    const possibleData = (error as { data?: { message?: unknown } }).data;
    if (possibleData && typeof possibleData.message === "string") {
      return possibleData.message;
    }
  }

  return null;
}

function cleanConvexMessage(message: string): string {
  return message
    .trim()
    .replace(CONVEX_UNCAUGHT_PREFIX, "")
    .replace(CONVEX_CLASS_PREFIX, "")
    .replace(GENERIC_ERROR_PREFIX, "")
    .replace(CONVEX_DETAILS_SUFFIX, "")
    .replace(CONVEX_ERROR_CODE_PREFIX, "")
    .trim();
}

export function normalizeOtpError(
  error: unknown,
  fallbackMessage = "Verification failed. Please try again."
): NormalizedOtpError {
  const rawMessage = extractErrorMessage(error);
  const reportError = (message: string) => {
    if (!rawMessage) return;
    reportHandledWebClientError({
      type: "web_auth_handled_error",
      safeMessage: message,
      error,
      context: {
        fallbackMessage,
      },
    });
  };

  if (!rawMessage) {
    return {
      message: fallbackMessage,
      suggestsResend: false,
      isExpired: false,
      isAlreadyUsed: false,
    };
  }

  const cleanedMessage = cleanConvexMessage(rawMessage);
  const normalizedMessage = cleanedMessage.toLowerCase();

  const isExpired =
    normalizedMessage.includes("otp has expired") ||
    normalizedMessage.includes("code has expired") ||
    normalizedMessage.includes("verification code has expired");

  const isAlreadyUsed =
    normalizedMessage.includes("already been used") ||
    normalizedMessage.includes("already used");

  const maxAttemptsReached =
    normalizedMessage.includes("maximum verification attempts exceeded") ||
    normalizedMessage.includes("maximum attempts exceeded");

  const noActiveOtp =
    normalizedMessage.includes("no otp found") ||
    normalizedMessage.includes("no verification code");

  if (isExpired) {
    const message = "The verification code has expired. Please request a new code.";
    reportError(message);
    return {
      message,
      suggestsResend: true,
      isExpired: true,
      isAlreadyUsed: false,
    };
  }

  if (isAlreadyUsed) {
    const message = "This verification code has already been used. Please request a new code.";
    reportError(message);
    return {
      message,
      suggestsResend: true,
      isExpired: false,
      isAlreadyUsed: true,
    };
  }

  if (maxAttemptsReached) {
    const message = "Maximum verification attempts reached. Please request a new code.";
    reportError(message);
    return {
      message,
      suggestsResend: true,
      isExpired: false,
      isAlreadyUsed: false,
    };
  }

  if (noActiveOtp) {
    const message = "No active verification code was found. Please request a new code.";
    reportError(message);
    return {
      message,
      suggestsResend: true,
      isExpired: false,
      isAlreadyUsed: false,
    };
  }

  const message = sanitizeUserFacingErrorMessage(
    cleanedMessage || rawMessage,
    fallbackMessage,
  );
  reportError(message);
  return {
    message,
    suggestsResend: false,
    isExpired: false,
    isAlreadyUsed: false,
  };
}
